import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaLinkedin } from "react-icons/fa";
import { PageHeader, SectionHeading } from "../components/ui";

const team = [
  {
    name: "Saurabh Mishra",
    title: "Founder & President",
    bio: "Saurabh brings more than two decades of experience leading semiconductor engineering teams and complex ASIC, SoC and embedded programs. At Chiparama, he sets the technical and delivery standard—building senior-led teams around the milestone, strengthening collaboration across US and India operations, and keeping execution grounded in engineering reality.",
    image: "/saurabh.jpg",
    linkedin: "https://www.linkedin.com/in/saurabh-mishra-7696417",
  },
  {
    name: "Prashant Gautam",
    title: "Co-Founder",
    bio: "Prashant is a semiconductor engineering and business leader focused on building delivery partnerships across RTL, design verification, physical design, DFT and STA. As Co-Founder, he leads growth and operating execution—translating client needs into the right engagement model, assembling domain-fit teams and maintaining clear accountability from first conversation through delivery.",
    image: "/Prashant.jpeg",
    linkedin: "https://www.linkedin.com/in/prashant-gautam-chiparama",
  },
  {
    name: "Nupur Kaudan",
    title: "Head of GTM & Growth",
    bio: "Nupur is a GTM strategist and commercial leader with 16 years of experience building revenue and market presence across data, technology and intelligence businesses in India and Asia Pacific. She has led market segmentation, positioning, enterprise pipeline development and C-suite engagement at organisations including NielsenIQ and IDC, advised early-stage technology ventures, and holds an MBA from the University of Chicago Booth School of Business. At Chiparama, she leads how the company takes its semiconductor engineering story to market and builds durable client relationships.",
    image: "/nupur.jpg",
    linkedin: "https://in.linkedin.com/in/nupur-kaudan",
  },
];

const principles = [
  { title: "Domain authority", body: "Work is scoped, screened and delivered by practitioners who have done it before." },
  { title: "Engineering partnership", body: "We integrate with your tools, cadence and technical leadership, not around them." },
  { title: "Precision and trust", body: "Clear ownership, measurable deliverables and IP-conscious delivery from day one." },
];

const DESCRIPTION =
  "Meet the Chiparama team combining US program leadership, PAN-India semiconductor depth and accountable engineering execution.";

export default function CompanyPage() {
  return (
    <>
      <Helmet>
        <title>About Chiparama | Leadership & Company</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href="https://chiparama.com/company" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About Chiparama | Leadership & Company" />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content="https://chiparama.com/company" />
        <meta property="og:image" content="https://chiparama.com/chiparamalogo.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About Chiparama | Leadership & Company" />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://chiparama.com/chiparamalogo.jpg" />
      </Helmet>

      <PageHeader eyebrow="About Chiparama" title="Built by engineers. Designed for engineering teams.">
        Critical semiconductor work cannot be staffed or delivered through keyword matching. It
        requires practitioners who understand the milestone, the dependencies and what good execution
        looks like.
      </PageHeader>

      <section className="section bg-white">
        <div className="container-x grid gap-12 lg:grid-cols-[1fr_1fr] lg:gap-20">
          <div>
            <SectionHeading eyebrow="Our story" title="One standard, practical and measurable." />
          </div>
          <div className="space-y-5 text-lg leading-relaxed text-slate-600">
            <p>
              We combine US-based program leadership with engineering depth across India to deliver
              defined SOW teams, specialist augmentation and build-operate-transfer engagements across
              the silicon lifecycle.
            </p>
            <p>
              Our standard is practical: the right domain expertise, clear ownership, measurable
              deliverables and communication that helps the client team move faster.
            </p>
          </div>
        </div>
        <div className="container-x mt-4 grid gap-5 md:grid-cols-3">
          {principles.map((p) => (
            <div key={p.title} className="card">
              <h3 className="text-lg font-semibold text-ink">{p.title}</h3>
              <p className="mt-3 leading-relaxed text-slate-600">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="section bg-paper">
        <div className="container-x">
          <SectionHeading eyebrow="Leadership" title="The people behind the delivery." />
          <div className="grid gap-6 lg:grid-cols-3">
            {team.map((m) => (
              <article key={m.name} className="group flex flex-col overflow-hidden rounded-3xl border border-slate-200 bg-white transition duration-300 hover:-translate-y-1 hover:border-brand/40 hover:shadow-[0_28px_70px_-28px_rgba(36,87,245,0.4)]">
                <div className="duotone duotone-people aspect-[4/4.2]">
                  <img
                    src={m.image}
                    alt={m.name}
                    className="h-full w-full object-cover object-top group-hover:scale-105"
                    width="480"
                    height="500"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-1 flex-col p-8">
                  <h3 className="font-display text-2xl font-semibold tracking-tight text-ink">{m.name}</h3>
                  <p className="mt-1.5 font-mono text-[11px] uppercase tracking-[0.18em] text-brand">{m.title}</p>
                  <p className="mt-5 flex-1 text-sm leading-relaxed text-slate-600">{m.bio}</p>
                  <a
                    href={m.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-brand"
                    aria-label={`${m.name} on LinkedIn`}
                  >
                    <FaLinkedin className="h-4 w-4" aria-hidden="true" /> LinkedIn
                  </a>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="on-dark section bg-navy-900 text-white">
        <div className="container-x grid items-center gap-10 lg:grid-cols-[1.2fr_0.8fr]">
          <div>
            <p className="eyebrow mb-4">One company. Two buyer paths.</p>
            <h2 className="h-section">Specialist team execution, applied where it matters.</h2>
            <p className="lede mt-5 max-w-2xl">
              For semiconductor clients, that means accountable engineering delivery across the silicon
              lifecycle. For tech startups, it means planning, building and operating the workforce
              layer without forcing founders to create a full internal function too early. The audiences
              differ; the standard does not: domain fit, clear ownership and accountable delivery.
            </p>
          </div>
          <div className="flex flex-col gap-3 lg:items-end">
            <Link to="/services" className="btn-primary w-full lg:w-auto">Semiconductor Engineering</Link>
            <Link to="/startup-workforce" className="btn-outline-dark w-full lg:w-auto">Startup Workforce</Link>
          </div>
        </div>
      </section>

      <section className="section-tight bg-white">
        <div className="container-x flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
          <h2 className="text-2xl font-semibold text-ink md:text-3xl">Ready to discuss an engineering need?</h2>
          <Link to="/contact" className="btn-primary px-7 py-3.5">Get in touch</Link>
        </div>
      </section>
    </>
  );
}
